<template>
  <form @submit.prevent="submitForm" class="space-y-8">
    <!-- Form Sections -->
    <div class="space-y-8">
      
      <!-- Basic Information Section -->
      <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div class="p-6 border-b border-slate-50 bg-slate-50/50">
          <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
            <div class="w-1.5 h-6 bg-emerald-500 rounded-full"></div>
            Basic Identification
          </h3>
        </div>
        
        <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Species -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Species <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
                <FontAwesomeIcon icon="paw" />
              </div>
              <select
                v-model="animalForm.species"
                required
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled>Select Species</option>
                <optgroup label="Mammals">
                  <option value="Cattle">Cattle</option>
                  <option value="Goats">Goats</option>
                  <option value="Sheep">Sheep</option>
                  <option value="Pigs">Pigs</option>
                  <option value="Camels">Camels</option>
                </optgroup>
                <optgroup label="Poultry">
                  <option value="Chickens">Chickens</option>
                  <option value="Ducks">Ducks</option>
                  <option value="Turkeys">Turkeys</option>
                </optgroup>
                <option value="Other">Other</option>
              </select>
              <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
                <FontAwesomeIcon icon="chevron-down" class="text-xs" />
              </div>
            </div>
            <span v-if="animalErrors.species" class="text-rose-500 text-[10px] font-black uppercase tracking-tighter ml-1">{{ animalErrors.species }}</span>
          </div>

          <!-- Breed -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Breed / Variety</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
                <FontAwesomeIcon icon="dna" />
              </div>
              <input
                v-model="animalForm.breed"
                type="text"
                placeholder="e.g. Holstein Friesian"
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
              />
            </div>
          </div>

          <!-- Gender -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Gender <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
                <FontAwesomeIcon icon="venus-mars" />
              </div>
              <select
                v-model="animalForm.gender"
                required
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled>Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Neutered">Neutered / Castrated</option>
              </select>
              <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
                <FontAwesomeIcon icon="chevron-down" class="text-xs" />
              </div>
            </div>
          </div>

          <!-- Count -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Quantity / Count <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
                <FontAwesomeIcon icon="layer-group" />
              </div>
              <input
                v-model.number="animalForm.count"
                type="number"
                min="1"
                required
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Logistics Section -->
      <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div class="p-6 border-b border-slate-50 bg-slate-50/50">
          <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
            <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
            Acquisition & Location
          </h3>
        </div>
        
        <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Acquisition Date -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Acquisition Date <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
                <FontAwesomeIcon icon="calendar-day" />
              </div>
              <input
                v-model="animalForm.acquisition_date"
                type="date"
                required
                :max="today"
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
              />
            </div>
          </div>

          <!-- Acquisition Method -->
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Acquisition Method <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
                <FontAwesomeIcon icon="truck-ramp-box" />
              </div>
              <select
                v-model="animalForm.acquisition_method"
                required
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled>Select Method</option>
                <option value="Purchase">Purchase</option>
                <option value="Birth">Birth on Farm</option>
                <option value="Gift">Gifted</option>
                <option value="Exchange">Exchange</option>
              </select>
              <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
                <FontAwesomeIcon icon="chevron-down" class="text-xs" />
              </div>
            </div>
          </div>

          <!-- Location -->
          <div class="col-span-1 md:col-span-2 space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Specific Location <span class="text-rose-500">*</span></label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
                <FontAwesomeIcon icon="map-location-dot" />
              </div>
              <input
                v-model="animalForm.location"
                type="text"
                required
                placeholder="e.g. South Pasture, Pen #4"
                class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Status Section -->
      <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div class="p-6 border-b border-slate-50 bg-slate-50/50">
          <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
            <div class="w-1.5 h-6 bg-orange-500 rounded-full"></div>
            Initial Health Status
          </h3>
        </div>
        
        <div class="p-8 space-y-8">
          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Current Condition <span class="text-rose-500">*</span></label>
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button 
                v-for="status in healthStatuses" 
                :key="status"
                type="button"
                @click="animalForm.health_status = status"
                class="px-4 py-4 rounded-2xl font-black text-sm transition-all border-2 flex flex-col items-center gap-2"
                :class="[
                  animalForm.health_status === status
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-slate-50 bg-slate-50 text-slate-400 hover:border-slate-200 hover:bg-white'
                ]"
              >
                <FontAwesomeIcon 
                  :icon="status === 'Healthy' ? 'check-circle' : status === 'Sick' ? 'virus' : status === 'Injured' ? 'bandage' : 'shield-virus'" 
                  class="text-xl"
                />
                {{ status }}
              </button>
            </div>
          </div>

          <!-- Symptoms (Conditional) -->
          <div v-if="animalForm.health_status === 'Sick' || animalForm.health_status === 'Injured'" class="mt-4 space-y-2 animate-fadeIn">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Observations / Symptoms</label>
            <div class="relative group">
              <textarea
                v-model="animalForm.symptoms"
                rows="3"
                placeholder="Provide detailed observations about the animal's condition..."
                class="block w-full px-5 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 text-slate-700 font-bold transition-all"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div class="mt-6">
       <transition
         enter-active-class="transition duration-300 ease-out"
         enter-from-class="transform -translate-y-4 opacity-0"
         enter-to-class="transform translate-y-0 opacity-100"
       >
         <div v-if="success" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
           <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
           {{ success }}
         </div>
       </transition>
       
       <transition
         enter-active-class="transition duration-300 ease-out"
         enter-from-class="transform -translate-y-4 opacity-0"
         enter-to-class="transform translate-y-0 opacity-100"
       >
         <div v-if="error" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
           <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
           {{ error }}
         </div>
       </transition>
    </div>

    <!-- Submit Button -->
    <div class="flex justify-end pt-4 border-t border-slate-100">
      <button
        type="submit"
        :disabled="loading"
        class="w-full md:w-auto bg-slate-900 text-white font-black px-10 py-4 rounded-2xl shadow-xl shadow-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-900/10 disabled:bg-slate-200 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-3 active:scale-95"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="paper-plane" />
        {{ loading ? 'Processing Registration...' : 'Register Individual / Flock' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { animalService } from '@/api/animal'
import type { Animal } from '@/api/animal'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faPaw,
  faDna,
  faVenusMars,
  faLayerGroup,
  faCalendarDay,
  faTruckRampBox,
  faMapLocationDot,
  faCheckCircle,
  faVirus,
  faBandage,
  faShieldVirus,
  faCircleExclamation,
  faCircleNotch,
  faPaperPlane,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faPaw,
  faDna,
  faVenusMars,
  faLayerGroup,
  faCalendarDay,
  faTruckRampBox,
  faMapLocationDot,
  faCheckCircle,
  faVirus,
  faBandage,
  faShieldVirus,
  faCircleExclamation,
  faCircleNotch,
  faPaperPlane,
  faChevronDown
)

// Define emits
const emit = defineEmits<{
  success: []
}>()

type Gender = 'Male' | 'Female' | 'Neutered' | 'Other' | ''
type AcquisitionMethod = 'Purchase' | 'Birth' | 'Gift' | 'Exchange' | ''
type HealthStatus = 'Healthy' | 'Sick' | 'Injured' | 'Quarantined' | ''

interface AnimalForm {
  species: string
  breed: string
  gender: Gender
  neutered_date: string | null
  acquisition_date: string
  acquisition_method: AcquisitionMethod
  health_status: HealthStatus
  symptoms: string | null
  location: string
  count: number
}

interface AnimalErrors {
  species?: string
  breed?: string
  gender?: string
  acquisition_date?: string
  acquisition_method?: string
  health_status?: string
  location?: string
  count?: string
}

const today = new Date().toISOString().split('T')[0]
const healthStatuses: HealthStatus[] = ['Healthy', 'Sick', 'Injured', 'Quarantined']

const animalForm = reactive<AnimalForm>({
  species: '',
  breed: '',
  gender: '',
  neutered_date: '',
  acquisition_date: today,
  acquisition_method: '',
  health_status: '' as HealthStatus,
  symptoms: '',
  location: '',
  count: 1,
})

const animalErrors = reactive<AnimalErrors>({})
const loading = ref(false)
const success = ref('')
const error = ref('')

function validateForm(): boolean {
  const errors: AnimalErrors = {}

  if (!animalForm.species) errors.species = 'Species is required'
  if (!animalForm.gender) errors.gender = 'Gender is required'
  if (!animalForm.acquisition_date) errors.acquisition_date = 'Acquisition date is required'
  if (!animalForm.acquisition_method) errors.acquisition_method = 'Acquisition method is required'
  if (!animalForm.health_status) errors.health_status = 'Health status is required'
  if (!animalForm.location) errors.location = 'Location is required'
  if (!animalForm.count || animalForm.count < 1) errors.count = 'Count must be at least 1'

  Object.assign(animalErrors, errors)
  return Object.keys(errors).length === 0
}

async function submitForm() {
  if (!validateForm()) return

  loading.value = true
  success.value = ''
  error.value = ''

  try {
    const payload: Omit<Animal, 'id' | 'created_by' | 'owner'> = {
      species: animalForm.species,
      breed: animalForm.breed,
      gender: animalForm.gender as 'Male' | 'Female' | 'Neutered' | 'Other',
      neutered_date: animalForm.gender === 'Neutered' ? animalForm.neutered_date || null : null,
      acquisition_date: animalForm.acquisition_date,
      acquisition_method: animalForm.acquisition_method as
        | 'Purchase'
        | 'Birth'
        | 'Gift'
        | 'Exchange',
      health_status: animalForm.health_status as 'Healthy' | 'Sick' | 'Injured' | 'Quarantined',
      symptoms: animalForm.health_status === 'Sick' ? animalForm.symptoms || undefined : undefined,
      location: animalForm.location,
      count: animalForm.count,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    await animalService.registerAnimal(payload)
    success.value = 'Animal registered successfully!'
    
    // Emit success event to parent
    emit('success')

    // Reset form
    Object.assign(animalForm, {
      species: '',
      breed: '',
      gender: '',
      neutered_date: '',
      acquisition_date: today,
      acquisition_method: '',
      health_status: '',
      symptoms: '',
      location: '',
      count: 1,
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to register animal'
  } finally {
    loading.value = false
  }
}
</script>
